import { useState } from 'react';
import Layout from '../../../components/feature/Layout';

interface Task {
  id: string;
  operacion: string;
  cliente: string;
  descripcion: string;
  responsable: string;
  fechaLimite: string;
  estado: 'pendiente' | 'en_proceso' | 'finalizada';
  prioridad: 'normal' | 'alta' | 'urgente';
}

export default function OperationsTasks() {
  const [selectedFilter, setSelectedFilter] = useState('todas');
  const [showNewTaskModal, setShowNewTaskModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  const tasks: Task[] = [
    {
      id: '1',
      operacion: 'OP-2024-1845',
      cliente: 'Importadora Global S.A.',
      descripcion: 'Validar certificado de origen con proveedor',
      responsable: 'María González',
      fechaLimite: '2024-01-15',
      estado: 'pendiente',
      prioridad: 'urgente'
    },
    {
      id: '2',
      operacion: 'OP-2024-1843',
      cliente: 'Exportadora del Sur Ltda.',
      descripcion: 'Coordinar inspección física con aduana',
      responsable: 'Carlos Ramírez',
      fechaLimite: '2024-01-16',
      estado: 'en_proceso',
      prioridad: 'alta'
    },
    {
      id: '3',
      operacion: 'OP-2024-1840',
      cliente: 'Comercial Andina S.A.C.',
      descripcion: 'Solicitar rectificación de factura comercial',
      responsable: 'Ana Torres',
      fechaLimite: '2024-01-14',
      estado: 'pendiente',
      prioridad: 'urgente'
    },
    {
      id: '4',
      operacion: 'OP-2024-1838',
      cliente: 'Distribuidora Pacífico E.I.R.L.',
      descripcion: 'Confirmar disponibilidad de transporte terrestre',
      responsable: 'Luis Mendoza',
      fechaLimite: '2024-01-17',
      estado: 'en_proceso',
      prioridad: 'normal'
    },
    {
      id: '5',
      operacion: 'OP-2024-1835',
      cliente: 'Logística Internacional Corp.',
      descripcion: 'Actualizar tracking de contenedor',
      responsable: 'María González',
      fechaLimite: '2024-01-13',
      estado: 'finalizada',
      prioridad: 'normal'
    },
    {
      id: '6',
      operacion: 'OP-2024-1832',
      cliente: 'Importadora Global S.A.',
      descripcion: 'Gestionar pago de aranceles',
      responsable: 'Carlos Ramírez',
      fechaLimite: '2024-01-18',
      estado: 'pendiente',
      prioridad: 'alta'
    },
    {
      id: '7',
      operacion: 'OP-2024-1830',
      cliente: 'Exportadora del Sur Ltda.',
      descripcion: 'Enviar documentación digitalizada al cliente',
      responsable: 'Ana Torres',
      fechaLimite: '2024-01-12',
      estado: 'finalizada',
      prioridad: 'normal'
    },
    {
      id: '8',
      operacion: 'OP-2024-1828',
      cliente: 'Comercial Andina S.A.C.',
      descripcion: 'Coordinar retiro de mercancía del almacén',
      responsable: 'Luis Mendoza',
      fechaLimite: '2024-01-19',
      estado: 'pendiente',
      prioridad: 'normal'
    }
  ];

  const getEstadoColor = (estado: string) => {
    switch (estado) {
      case 'pendiente': return 'bg-[#EAB308]/10 text-[#EAB308]';
      case 'en_proceso': return 'bg-[#2F80ED]/10 text-[#2F80ED]';
      case 'finalizada': return 'bg-[#10B981]/10 text-[#10B981]';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getEstadoIcon = (estado: string) => {
    switch (estado) {
      case 'pendiente': return 'ri-time-line';
      case 'en_proceso': return 'ri-loader-4-line';
      case 'finalizada': return 'ri-check-line';
      default: return 'ri-circle-line';
    }
  };

  const getPrioridadColor = (prioridad: string) => {
    switch (prioridad) {
      case 'urgente': return 'bg-[#EF4444] text-white';
      case 'alta': return 'bg-[#F59E0B] text-white';
      case 'normal': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const filteredTasks = tasks.filter(task => {
    if (selectedFilter === 'todas') return true;
    if (selectedFilter === 'mis_tareas') return task.responsable === 'María González';
    if (selectedFilter === 'vencidas') {
      const today = new Date();
      const fechaLimite = new Date(task.fechaLimite);
      return fechaLimite < today && task.estado !== 'finalizada';
    }
    return task.estado === selectedFilter;
  });

  const stats = {
    total: tasks.length,
    pendientes: tasks.filter(t => t.estado === 'pendiente').length,
    enProceso: tasks.filter(t => t.estado === 'en_proceso').length,
    finalizadas: tasks.filter(t => t.estado === 'finalizada').length,
    vencidas: tasks.filter(t => {
      const today = new Date();
      const fechaLimite = new Date(t.fechaLimite);
      return fechaLimite < today && t.estado !== 'finalizada';
    }).length
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#0A2A43]">Tareas Operativas</h1>
            <p className="text-sm text-[#6B7280] mt-1">Gestión de tareas y actividades por operación</p>
          </div>
          <button
            onClick={() => setShowNewTaskModal(true)}
            className="px-4 py-2.5 bg-gradient-to-r from-[#114B7A] to-[#2F80ED] text-white rounded-md hover:shadow-md cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line mr-2"></i>Nueva Tarea
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-5 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-[#6B7280]">Total Tareas</span>
              <div className="w-8 h-8 rounded-lg bg-[#2F80ED]/10 flex items-center justify-center">
                <i className="ri-task-line text-[#2F80ED]"></i>
              </div>
            </div>
            <p className="text-2xl font-bold text-[#0A2A43]">{stats.total}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-[#6B7280]">Pendientes</span>
              <div className="w-8 h-8 rounded-lg bg-[#EAB308]/10 flex items-center justify-center">
                <i className="ri-time-line text-[#EAB308]"></i>
              </div>
            </div>
            <p className="text-2xl font-bold text-[#EAB308]">{stats.pendientes}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-[#6B7280]">En Proceso</span>
              <div className="w-8 h-8 rounded-lg bg-[#2F80ED]/10 flex items-center justify-center">
                <i className="ri-loader-4-line text-[#2F80ED]"></i>
              </div>
            </div>
            <p className="text-2xl font-bold text-[#2F80ED]">{stats.enProceso}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-[#6B7280]">Finalizadas</span>
              <div className="w-8 h-8 rounded-lg bg-[#10B981]/10 flex items-center justify-center">
                <i className="ri-check-line text-[#10B981]"></i>
              </div>
            </div>
            <p className="text-2xl font-bold text-[#10B981]">{stats.finalizadas}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-[#6B7280]">Vencidas</span>
              <div className="w-8 h-8 rounded-lg bg-[#EF4444]/10 flex items-center justify-center">
                <i className="ri-error-warning-line text-[#EF4444]"></i>
              </div>
            </div>
            <p className="text-2xl font-bold text-[#EF4444]">{stats.vencidas}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSelectedFilter('todas')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                selectedFilter === 'todas'
                  ? 'bg-gradient-to-r from-[#114B7A] to-[#2F80ED] text-white'
                  : 'bg-gray-100 text-[#6B7280] hover:bg-gray-200'
              }`}
            >
              Todas ({stats.total})
            </button>
            <button
              onClick={() => setSelectedFilter('mis_tareas')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                selectedFilter === 'mis_tareas'
                  ? 'bg-gradient-to-r from-[#114B7A] to-[#2F80ED] text-white'
                  : 'bg-gray-100 text-[#6B7280] hover:bg-gray-200'
              }`}
            >
              Mis Tareas
            </button>
            <button
              onClick={() => setSelectedFilter('pendiente')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                selectedFilter === 'pendiente'
                  ? 'bg-[#EAB308] text-white'
                  : 'bg-gray-100 text-[#6B7280] hover:bg-gray-200'
              }`}
            >
              Pendientes ({stats.pendientes})
            </button>
            <button
              onClick={() => setSelectedFilter('en_proceso')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                selectedFilter === 'en_proceso'
                  ? 'bg-[#2F80ED] text-white'
                  : 'bg-gray-100 text-[#6B7280] hover:bg-gray-200'
              }`}
            >
              En Proceso ({stats.enProceso})
            </button>
            <button
              onClick={() => setSelectedFilter('finalizada')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                selectedFilter === 'finalizada'
                  ? 'bg-[#10B981] text-white'
                  : 'bg-gray-100 text-[#6B7280] hover:bg-gray-200'
              }`}
            >
              Finalizadas ({stats.finalizadas})
            </button>
            <button
              onClick={() => setSelectedFilter('vencidas')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                selectedFilter === 'vencidas'
                  ? 'bg-[#EF4444] text-white'
                  : 'bg-gray-100 text-[#6B7280] hover:bg-gray-200'
              }`}
            >
              Vencidas ({stats.vencidas})
            </button>
          </div>
        </div>

        {/* Tasks Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[#6B7280] uppercase">Operación</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[#6B7280] uppercase">Descripción</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[#6B7280] uppercase">Responsable</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[#6B7280] uppercase">Fecha Límite</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[#6B7280] uppercase">Estado</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[#6B7280] uppercase">Prioridad</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-[#6B7280] uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredTasks.map((task) => (
                  <tr key={task.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-4">
                      <div>
                        <p className="text-sm font-semibold text-[#0A2A43]">{task.operacion}</p>
                        <p className="text-xs text-[#6B7280]">{task.cliente}</p>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-sm text-[#0A2A43]">{task.descripcion}</p>
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-sm text-[#0A2A43]">{task.responsable}</p>
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-sm text-[#0A2A43]">{new Date(task.fechaLimite).toLocaleDateString('es-ES')}</p>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getEstadoColor(task.estado)}`}>
                        <i className={getEstadoIcon(task.estado)}></i>
                        {task.estado === 'pendiente' ? 'Pendiente' : task.estado === 'en_proceso' ? 'En Proceso' : 'Finalizada'}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${getPrioridadColor(task.prioridad)}`}>
                        {task.prioridad === 'urgente' ? 'URGENTE' : task.prioridad === 'alta' ? 'ALTA' : 'NORMAL'}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => {
                            setSelectedTask(task);
                            setShowDetailModal(true);
                          }}
                          className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-gray-100 cursor-pointer"
                          title="Ver detalle"
                        >
                          <i className="ri-eye-line text-[#6B7280]"></i>
                        </button>
                        <button className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-gray-100 cursor-pointer" title="Editar">
                          <i className="ri-edit-line text-[#6B7280]"></i>
                        </button>
                        {task.estado !== 'finalizada' && (
                          <button className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-[#10B981]/10 cursor-pointer" title="Marcar como finalizada">
                            <i className="ri-check-line text-[#10B981]"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* New Task Modal */}
      {showNewTaskModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl mx-4">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-[#0A2A43]">Nueva Tarea Operativa</h2>
              <button onClick={() => setShowNewTaskModal(false)} className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-xl text-[#6B7280]"></i>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Operación Asociada *</label>
                <select className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2F80ED]">
                  <option>Seleccionar operación...</option>
                  <option>OP-2024-1845 - Importadora Global S.A.</option>
                  <option>OP-2024-1843 - Exportadora del Sur Ltda.</option>
                  <option>OP-2024-1840 - Comercial Andina S.A.C.</option>
                  <option>OP-2024-1838 - Distribuidora Pacífico E.I.R.L.</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Descripción de la Tarea *</label>
                <textarea rows={3} placeholder="Describe la tarea a realizar..." className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2F80ED]"></textarea>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Responsable *</label>
                  <select className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2F80ED]">
                    <option>Juan Delgado</option>
                    <option>María González</option>
                    <option>Carlos Ramírez</option>
                    <option>Ana Torres</option>
                    <option>Luis Mendoza</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Fecha Límite *</label>
                  <input type="date" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2F80ED]" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Prioridad *</label>
                  <select className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2F80ED]">
                    <option>Normal</option>
                    <option>Alta</option>
                    <option>Urgente</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Estado Inicial</label>
                  <select className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2F80ED]">
                    <option>Pendiente</option>
                    <option>En Proceso</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button onClick={() => setShowNewTaskModal(false)} className="px-4 py-2.5 border border-gray-200 text-[#6B7280] rounded-md hover:bg-gray-50 cursor-pointer whitespace-nowrap">
                Cancelar
              </button>
              <button
                onClick={() => {
                  setShowNewTaskModal(false);
                  alert('Tarea creada exitosamente');
                }}
                className="px-4 py-2.5 bg-gradient-to-r from-[#114B7A] to-[#2F80ED] text-white rounded-md hover:shadow-md cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line mr-2"></i>Crear Tarea
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedTask && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl mx-4">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-[#0A2A43]">{selectedTask.operacion}</h2>
                <p className="text-sm text-[#6B7280]">{selectedTask.cliente}</p>
              </div>
              <button onClick={() => setShowDetailModal(false)} className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-xl text-[#6B7280]"></i>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">Descripción</label>
                <p className="text-sm text-[#0A2A43]">{selectedTask.descripcion}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-1">Responsable</label>
                  <p className="text-sm font-medium text-[#0A2A43]">{selectedTask.responsable}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-1">Fecha Límite</label>
                  <p className="text-sm font-medium text-[#0A2A43]">{new Date(selectedTask.fechaLimite).toLocaleDateString('es-ES')}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-1">Estado</label>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getEstadoColor(selectedTask.estado)}`}>
                    <i className={getEstadoIcon(selectedTask.estado)}></i>
                    {selectedTask.estado === 'pendiente' ? 'Pendiente' : selectedTask.estado === 'en_proceso' ? 'En Proceso' : 'Finalizada'}
                  </span>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-1">Prioridad</label>
                  <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${getPrioridadColor(selectedTask.prioridad)}`}>
                    {selectedTask.prioridad === 'urgente' ? 'URGENTE' : selectedTask.prioridad === 'alta' ? 'ALTA' : 'NORMAL'}
                  </span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Actualizar Estado</label>
                <select className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2F80ED]">
                  <option>Pendiente</option>
                  <option>En Proceso</option>
                  <option>Finalizada</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Comentarios</label>
                <textarea rows={3} placeholder="Agrega comentarios sobre el progreso..." className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2F80ED]"></textarea>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button onClick={() => setShowDetailModal(false)} className="px-4 py-2.5 border border-gray-200 text-[#6B7280] rounded-md hover:bg-gray-50 cursor-pointer whitespace-nowrap">
                Cerrar
              </button>
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  alert('Tarea actualizada correctamente');
                }}
                className="px-4 py-2.5 bg-gradient-to-r from-[#114B7A] to-[#2F80ED] text-white rounded-md hover:shadow-md cursor-pointer whitespace-nowrap"
              >
                <i className="ri-save-line mr-2"></i>Guardar Cambios
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
